/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Deva Sen. & Warren Dowey
 */

import { Injectable } from '@angular/core';
import {
  Router,
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  CanActivateChild,
} from '@angular/router';

import { AuthenticationService } from './authentication.service';
import { ComponentPermissionsListService } from '@app/services/api-services/component-permissions/component-permissions-list.service';
import { OrganisationUserService } from '@app/services/api-services/organisation-users/organisation-user.service';
import { OrganisationUsersModel } from '@app/models/organisation-users.model';
import { ApiParamsModel } from '@app/models/api-params.model';
import { SettingsService } from '@app/services/api-services/settings/settings.service';
import { SettingsModel } from 'app/models/settings.model';
import { concatAll } from 'rxjs/operators';
import { Subscription, Observable } from 'rxjs';
import { SubSink } from '@base/node_modules//subsink/dist/subsink';
import { environment } from '@env/environment';
import { access } from 'fs';
import { DetailedProjectUserModel, ProjectUserPermissionsService } from '../shared-services/project-user-permissions.service';
import { MaterialSnackbarService } from '../shared-services/material-snackbar.service';
import { OrganisationCognitoUser } from 'app/models/cognito-organisation-user.model';

@Injectable()
export class AuthenticationGuard implements CanActivate, CanActivateChild {
  // Subsink to be used in the OnDestroy Lifecycle hook to destroy all active subscriptions
  private userSubscription: Subscription;
  private settingsSubscription: Subscription;
  private subscriptionContainer = new SubSink();

  private runAnalytics: boolean;
  private isSuperUser: boolean;

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private componentPermissionsListService: ComponentPermissionsListService,
    private organisationUserService: OrganisationUserService,
    private settingsService: SettingsService,
    private projectUserPermissionsService: ProjectUserPermissionsService,
    private notify: MaterialSnackbarService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    // Set componentPermissionsList
    if (this.authenticationService.user && this.componentPermissionsListService.componentPermissionsList.length === 0) {
      // this.componentPermissionsListService.getData(new ApiParamsModel());
    }
    if (state.url === '') {
      this.router.navigate(['/home'], {
        replaceUrl: true,
      });
      return true;
    }
    if (
      (state.url.indexOf('/login') !== -1 ||
        state.url.indexOf('/register') !== -1 ||
        state.url.indexOf('/forgot-password') !== -1 ||
        state.url.indexOf('mfa-entry') !== -1 ||
        state.url.indexOf('new-password') !== -1) &&
      this.authenticationService.isAuthenticated()
    ) {
      this.router.navigate(['/dashboard'], {
        replaceUrl: true,
      });
      return false;
    } else if (
      (state.url.indexOf('/login') !== -1 ||
        state.url.indexOf('/register') !== -1 ||
        state.url.indexOf('/forgot-password') !== -1 ||
        state.url.indexOf('mfa-entry') !== -1 ||
        state.url.indexOf('new-password') !== -1) &&
      !this.authenticationService.isAuthenticated()
    ) {
      return true;
    } else {
      if (this.authenticationService.isAuthenticated()) {
          return true;
      }
      this.router.navigate(['/login'], {
        queryParams: {
          redirect: state.url,
        },
        replaceUrl: true,
      });
      return false;
    }
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    let permitted = true;
    let accessPermission: string;
    let moduleActive: boolean; // This value is determined by the settings value for each module and whether or not they are active or disabled
    if (state.url === '') {
      this.router.navigate(['/home'], {
        replaceUrl: true,
      });
      return true;
    }
    // Check if user has run_analytics/sonrai_super_user for analytics/super admin access
    if (
      ((state.url.indexOf('/analytics') !== -1)
        || (state.url.indexOf('/admin/super-admin-dashboard') !== -1))
      && this.authenticationService.user
    ) {
      this.getOrganisationCognitoUserValues(this.authenticationService.user, state);
    }
    if (
      (state.url.indexOf('/login') !== -1 ||
        state.url.indexOf('/register') !== -1 ||
        state.url.indexOf('/forgot-password') !== -1 ||
        state.url.indexOf('mfa-entry') !== -1 ||
        state.url.indexOf('new-password') !== -1) &&
      this.authenticationService.isAuthenticated()
    ) {
      this.router.navigate(['/dashboard'], {
        replaceUrl: true,
      });
      return false;
    } else if (
      (state.url.indexOf('/login') !== -1 ||
        state.url.indexOf('/register') !== -1 ||
        state.url.indexOf('/forgot-password') !== -1 ||
        state.url.indexOf('mfa-entry') !== -1 ||
        state.url.indexOf('new-password') !== -1) &&
      !this.authenticationService.isAuthenticated()
    ) {
      return true;
    } else {
      if (this.authenticationService.isAuthenticated()) {
        switch (state.url) {
          case '/admin-dashboard':
          case '/admin/admin-user':
          case '/admin/admin-projects':
          case '/admin/admin-user-projects':
          case '/admin/admin-activity':
          case '/admin/admin-integrations':
          case '/admin/admin-settings':
            accessPermission = 'view_admin_screen';
            break;
          case '/projects':
            this.getSettings(environment.product_owner_uuid).subscribe((settings) => {
              // If setting is found and is set to true set moduleActive value to true
              if (settings.displayProjects === true) {
                accessPermission = 'view_projects_screen';
                moduleActive = true;
              } else {
                moduleActive = false;
              }
              // Do additional checks for projects as the module can be enabled or disabled
              if ((!accessPermission && moduleActive) || (this.authenticationService.permissions.some(permission => { return permission.componentActionKey === accessPermission }) && moduleActive) ) {
                permitted = true;
              } else {
                permitted = false;
              }
              if (!permitted) {
                this.router.navigate(['/dashboard'], {
                  replaceUrl: true,
                });
                return permitted;
              } else {
                return permitted;
              }
            });
            break;
          case '/datasets':
            accessPermission = 'view_datasets_screen';
            break;
        }
        // moduleActive check is not needed for other urls as the other modules can't be disabled
        if (moduleActive == null && state.url !== '/projects') {
          if (!accessPermission || this.authenticationService.permissions.some(permission => { return permission.componentActionKey === accessPermission })) {
            permitted = true;
          } else {
            permitted = false;
          }
        }
        if (state.url.split('/')[1] === 'project'
          && ['project-users'].includes(state.url.split('/')[3]))
        {
          this.projectUserPermissionsService.getUserPermissions(
            state.url.split('/')[2], this.authenticationService.user
          ).subscribe(projectUser => {
            switch (state.url.split('/')[3]) {
              case 'project-users':
                permitted = projectUser.rolePermissions.includes('manage_users');
                if (!permitted) {
                  this.notify.openSnackBar('You do not have permissions to manage project users', 'Ok');
                }
                this.router.navigate(['/dashboard'], {
                  replaceUrl: true,
                });
                break;
            }
          });
          if (!permitted) {
            this.router.navigate([state.url.substring(0, state.url.lastIndexOf('/'))], {
              replaceUrl: true,
            });
            return permitted;
          }
        }
        if (state.url === '/dashboard' || state.url === '/') {
          return true;
        }
        if (!permitted) {
          this.router.navigate(['/dashboard'], {
            replaceUrl: true,
          });
          return permitted;
        } else {
          return permitted;
        }
      }
      this.router.navigate(['/login'], {
        queryParams: {
          redirect: state.url,
        },
        replaceUrl: true,
      });
      return false;
    }
  }

  getSettings(organisationId) {
    return new Observable<SettingsModel>((subscriber) => {
      this.subscriptionContainer.sink = this.settingsSubscription = this.settingsService
        .getSettings(organisationId)
        .subscribe((data) => {
          let settings = data[0]
          let settingsModel = new SettingsModel(
            settings.organisation_id,
            settings.app_logo,
            settings.display_projects,
            settings.display_datasets,
            settings.display_cases,
            settings.display_imaging,
            settings.imaging_url,
            settings.display_data_ui_app
          )
          subscriber.next(settingsModel);
          if (this.settingsSubscription) {
            this.settingsSubscription.unsubscribe();
          }
        },
        (error) => {
          if (this.settingsSubscription) {
            this.settingsSubscription.unsubscribe();
          }
        }
      );
    });
  }

  // Run Analytics and Super User values are held on OrganisationCognitoUser
  getOrganisationCognitoUserValues(user: OrganisationCognitoUser, state: RouterStateSnapshot) {
    this.subscriptionContainer.sink = this.userSubscription = this.organisationUserService.getOrganisationUserByUserId(user.userId).subscribe(
      (userData) => {
        if(userData != null) {
          if (this.userSubscription) {
            // Set run_analytics & sonrai_super_user value
            this.runAnalytics = userData.runAnalytics;
            this.isSuperUser = userData.sonraiSuperUser;
            // Redirect user if they do not have run_analytics set to true
            if(
              (this.runAnalytics === false && (state.url.indexOf('/analytics') !== -1))
              || (this.isSuperUser === false && (state.url.indexOf('/admin/super-admin-dashboard') !== -1))
            ) {
              this.router.navigate(['/'], {
                replaceUrl: true,
              });
            }
            this.userSubscription.unsubscribe();
          }
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.subscriptionContainer.unsubscribe();
  }

}
